<template>
  <div class="w-full flex flex-col gap-2">
    <div class="brands-list w-full flex flex-col gap-1 min-h-0 overflow-hidden" :style="{ height: brandsListHeight }">
      <button v-for="(brand, index) in similarBrands" :ref="`brand-preview-${brand.id}`" :key="`brand-preview-${brand.id}-${index}`" 
      class="w-full flex items-center gap-2 p-1 rounded-md text-left min-w-0 transition-colors duration-100"
      :class="brandPreview.brand?.id === brand.id ? 'bg-neutral-50' : 'hover:bg-neutral-25'"
      @click="handleSelectBrand(brand)">
        <BrandAvatar :avatar-src="brand.avatar" :brand-name="brand.name" :width="24" :height="24" class="rounded" />
        <BaseText type="label" size="sm" class="flex-grow text-text-muted truncate">
          {{ brand.name }}
        </BaseText>
      </button>
    </div>
    <!-- View More Button -->
    <button v-if="similarBrands.length > 3" class="self-start py-0.5 px-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-50"
    @click="showAllBrands = !showAllBrands">
      <BaseText type="label" size="sm" class="text-text-muted">
        {{ showAllBrands ? 'View less' : 'View more' }}
      </BaseText>
    </button>
    <!-- Brand Preview Popup -->
    <transition>
      <div v-if="brandPreview.brand" class="brand-preview-popup flex flex-col p-3"
      :style="{ transform: brandPreviewTransform }" v-on-clickaway="() => { brandPreview = { brand: null, xPos: 0, yPos: 0 } }">
        <!-- Name and Avatar -->
        <div class="w-full flex items-center gap-2 min-w-0">
          <BrandAvatar :avatar-src="brandPreview.brand.avatar" :brand-name="brandPreview.brand.name" :width="24" :height="24" class="rounded" />
          <BaseText type="label" size="sm" class="flex-grow text-text-muted truncate">
            {{ brandPreview.brand.name }}
          </BaseText>
        </div>
        <!-- Description -->
        <BaseText v-if="brandPreview.brand.description" type="body" size="sm" class="description text-text-muted mt-2">
           {{ brandPreview.brand.description }}
        </BaseText>
        <!-- Links -->
        <div class="w-full flex flex-col gap-0.5 mt-2">
          <router-link v-if="brandPreview.brand.id"
          :to="{ name: 'SpyderView', params: { id: brandPreview.brand.id, view: 'overview' } }"
          class="w-full flex items-center gap-1.5 p-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-50">
            <SpyderIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted pl-1">
              View brand in Spyder
            </BaseText>
            <div class="transform -rotate-90 ml-auto">
              <ChevronIcon className="text-icon-normal" />
            </div>
          </router-link>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
import BrandAvatar from '../../brands/BrandAvatar.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import SpyderIcon from '../../globals/Icons/SidebarIcons/SpyderIcon.vue'

export default {
  name: 'SimilarBrandsPreview',
  mixins: [clickaway],
  components: {
    BrandAvatar,
    ChevronIcon,
    SpyderIcon
  },
  props: {
    /* Normalized by BrandOverviewView from a /similar search hit:
      {
        id: String,
        name: String,
        avatar: String | null,
        description: String | null,
        adLibraryId: String | null
      } */
    similarBrands: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      showAllBrands: false,
      brandPreview: {
        brand: null,
        xPos: 0,
        yPos: 0,
      }
    }
  },
  computed: {
    brandsListHeight () {
      const naturalHeight = this.similarBrands.length * 32 + (this.similarBrands.length - 1) * 4
      return `${this.showAllBrands ? naturalHeight : Math.min(naturalHeight, 104)}px`
    },
    brandPreviewTransform () {
      return `translate(${this.brandPreview.xPos}px, ${this.brandPreview.yPos}px) translateY(-50%)`
    }
  },
  methods: {
    handleSelectBrand (brand) {
      if (this.brandPreview.brand?.id === brand.id) {
        this.brandPreview = { brand: null, xPos: 0, yPos: 0 }
        return
      }

      this.brandPreview = { brand, xPos: 0, yPos: 0 }
      const brandPreview = this.$refs[`brand-preview-${brand.id}`][0]
      const brandPreviewRect = brandPreview.getBoundingClientRect()
      this.brandPreview.xPos = brandPreviewRect.left + brandPreviewRect.width + 8
      this.brandPreview.yPos = brandPreviewRect.top + brandPreviewRect.height / 2
    }
  }
}
</script>

<style scoped>
.brands-list {
  transition: height 200ms ease-in-out;
}
.brand-preview-popup {
  width: 308px;
  position: fixed;
  left: 0;
  top: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 24px 32px -12px rgba(53, 57, 75, 0.24), 0 1px 2px 0 rgba(162, 172, 186, 0.16), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: transform 150ms ease-in-out;
  z-index: 40001;
}
.brand-preview-popup .description {
  display: -webkit-box;
  line-clamp: 4;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>